// firestoreService.js
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from "firebase/firestore";
import { db, auth } from "../config/firebase";

/**
 * Get current user ID from Firebase Auth
 */
export async function getCurrentUserId() {
  return auth.currentUser?.uid;
}

/**
 * Create initial user document with your requested hierarchical structure
 */
export async function createUserDocument(user, firstName, subscriptionData = null) {
  // Create main user document (minimal data)
  const userRef = doc(db, "users", user.uid);
  const mainUserData = {
    uid: user.uid,
    email: user.email,
    createdAt: serverTimestamp()
  };
  await setDoc(userRef, mainUserData);
  
  // Create subscription/user details collection - profile document
  const profileRef = doc(db, "users", user.uid, "subscription", "profile");
  
  // Determine subscription plan - default to 'essential' if not specified
  const plan = subscriptionData?.plan || 'essential';
  
  const profileData = {
    displayName: firstName || user.email.split("@")[0],
    firstName: firstName || '',
    email: user.email,
    createdAt: serverTimestamp(),
    
    // Export counter data
    exportCount: 0,
    maxExportPerMonth: plan === 'pro' ? 75 : 10,
    lastResetDate: serverTimestamp(),
    
    // User preferences
    userSettings: {
      dontShowInteractive: false
    },
    
    // AI Usage tracking - moved from Azure to Firebase
    textCount: 0,
    imageCount: 0,
    maxTextPerMonth: plan === 'pro' ? 50 : 0,
    maxImagesPerMonth: plan === 'pro' ? 50 : 0,
    lastResetDate: serverTimestamp(),
    
    // Canvas and Asset Storage Limits (testing with 100KB for both)
    assetStorageCurrent: 0,
    assetStorageLimit: plan === 'pro' ? 15 * 1000 * 1000 * 1000 : 5 * 1000 * 1000 * 1000, // 15GB for pro, 5GB for essential
    canvasSavedCurrent: 0,
    canvasSavedLimit: plan === 'pro' ? 100 : 20, // 100 for pro, 20 for essential
    
    // Subscription information in same document as requested
    subscriptionPlan: plan,
    subscriptionStatus: subscriptionData?.status || (subscriptionData?.type === 'paid' ? 'active' : null),
    subscriptionType: subscriptionData?.type || null, // 'paid', 'trial', null
    stripeCustomerId: subscriptionData?.customerId || null,
    stripeSubscriptionId: subscriptionData?.subscriptionId || null,
    
    // Trial information
    trialStartDate: subscriptionData?.type === 'trial' ? serverTimestamp() : null,
    trialEndDate: subscriptionData?.type === 'trial' ? 
      new Date(Date.now() + 21 * 24 * 60 * 60 * 1000) : null,
    trialUsed: subscriptionData?.type === 'trial'
  };
  await setDoc(profileRef, profileData);
  
  return { mainUser: mainUserData, profile: profileData };
}

/**
 * Get user subscription plan using new hierarchical structure
 */
export async function getUserSubscriptionPlan(userId) {
  const profileRef = doc(db, "users", userId, "subscription", "profile");
  const profileSnap = await getDoc(profileRef);
  
  if (profileSnap.exists()) {
    const profileData = profileSnap.data();
    
    // Check if trial has expired
    if (profileData.subscriptionType === 'trial' && profileData.trialEndDate) {
      const trialEndDate = profileData.trialEndDate.toDate ? profileData.trialEndDate.toDate() : new Date(profileData.trialEndDate);
      const now = new Date();
      
      if (now > trialEndDate) {
        // Trial has expired, update profile document
        await updateDoc(profileRef, {
          subscriptionPlan: null,
          subscriptionStatus: 'expired',
          subscriptionType: null,
          lastModified: serverTimestamp()
        });
        return null;
      }
    }
    
    return profileData.subscriptionPlan;
  }
  
  return null;
}

/**
 * Get full user subscription details using new hierarchical structure
 */
export async function getUserSubscriptionDetails(userId) {
  const profileRef = doc(db, "users", userId, "subscription", "profile");
  const profileSnap = await getDoc(profileRef);
  
  if (profileSnap.exists()) {
    const profileData = profileSnap.data();
    
    // Check if trial has expired
    if (profileData.subscriptionType === 'trial' && profileData.trialEndDate) {
      const trialEndDate = profileData.trialEndDate.toDate ? profileData.trialEndDate.toDate() : new Date(profileData.trialEndDate);
      const now = new Date();
      
      if (now > trialEndDate) {
        // Trial has expired
        await updateDoc(profileRef, {
          subscriptionPlan: null,
          subscriptionStatus: 'expired',
          subscriptionType: null,
          lastModified: serverTimestamp()
        });
        
        return {
          plan: null,
          status: 'expired',
          type: null,
          trialExpired: true
        };
      }
      
      // Trial is still active, calculate days remaining
      const daysRemaining = Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24));
      
      return {
        plan: profileData.subscriptionPlan,
        status: profileData.subscriptionStatus,
        type: profileData.subscriptionType,
        trialDaysRemaining: daysRemaining,
        trialEndDate: trialEndDate,
        // Include AI usage fields
        textCount: profileData.textCount || 0,
        imageCount: profileData.imageCount || 0,
        maxTextPerMonth: profileData.maxTextPerMonth || 50,
        maxImagesPerMonth: profileData.maxImagesPerMonth || 50
      };
    }
    
    return {
      plan: profileData.subscriptionPlan,
      status: profileData.subscriptionStatus,
      type: profileData.subscriptionType,
      customerId: profileData.stripeCustomerId,
      subscriptionId: profileData.stripeSubscriptionId,
      // Include AI usage fields
      textCount: profileData.textCount || 0,
      imageCount: profileData.imageCount || 0,
      maxTextPerMonth: profileData.maxTextPerMonth || 50,
      maxImagesPerMonth: profileData.maxImagesPerMonth || 50
    };
  }
  
  return null;
}

/**
 * Update user subscription using new hierarchical structure
 */
export async function updateUserSubscription(userId, subscriptionData) {
  const profileRef = doc(db, "users", userId, "subscription", "profile");
  
  await updateDoc(profileRef, {
    subscriptionPlan: subscriptionData.plan,
    subscriptionActive: subscriptionData.active,
    lastModified: serverTimestamp()
  });
}

/**
 * Get user record by email from Firestore
 */
export async function getUserByEmail(email) {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {

    return null;
  }
}

/**
 * Check if email has had a trial
 */
export async function hasEmailHadTrial(email) {
  try {
    // Check expired trials collection first
    const expiredTrialsRef = collection(db, "expiredTrials");
    const expiredQuery = query(
      expiredTrialsRef, 
      where("email", "==", email.toLowerCase())
    );
    
    const expiredSnapshot = await getDocs(expiredQuery);
    return !expiredSnapshot.empty;
    
  } catch (error) {

    // If Firestore is unavailable, allow trial signup
    return false;
  }
}

/**
 * Record trial usage for user
 */
export async function recordTrialUsageForUser(userId) {
  const userRef = doc(db, "users", userId);
  
  await updateDoc(userRef, {
    trialUsed: true,
    trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
    lastModified: serverTimestamp()
  });
}

/**
 * Update user's storage usage by adding or subtracting bytes
 */
export async function updateUserStorageUsage(userId, bytesChange) {
  try {
    const profileRef = doc(db, "users", userId, "subscription", "profile");
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      const currentUsage = profileSnap.data().assetStorageCurrent || 0;
      const newUsage = Math.max(0, currentUsage + bytesChange); // Ensure usage doesn't go below 0
      
      await updateDoc(profileRef, {
        assetStorageCurrent: newUsage,
        lastModified: serverTimestamp()
      });
      

      return newUsage;
    } else {

      throw new Error('User profile not found');
    }
  } catch (error) {

    throw error;
  }
}

/**
 * Get user export count and check if reset is needed
 */
export async function getUserExportCount(userId) {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (userSnap.exists()) {
    const userData = userSnap.data();
    const currentDate = new Date();
    const lastResetDate = userData.lastResetDate?.toDate();
    const createdAt = userData.createdAt?.toDate();
    
    // Check if we need to reset (monthly reset based on creation date)
    if (lastResetDate && createdAt) {
      const nextResetDate = getNextResetDate(createdAt, lastResetDate);
      
      if (currentDate >= nextResetDate) {
        // Reset the counter
        await updateDoc(userRef, {
          exportCount: 0,
          lastResetDate: serverTimestamp(),
          lastModified: serverTimestamp()
        });
        
        return { exportCount: 0, needsReset: false };
      }
    }
    
    return { 
      exportCount: userData.exportCount || 0, 
      needsReset: false 
    };
  }
  
  return { exportCount: 0, needsReset: false };
}

/**
 * Increment user export count
 */
export async function incrementUserExportCount(userId) {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (userSnap.exists()) {
    const currentCount = userSnap.data().exportCount || 0;
    
    await updateDoc(userRef, {
      exportCount: currentCount + 1,
      lastModified: serverTimestamp()
    });
    
    return currentCount + 1;
  }
  
  return 1;
}

/**
 * Get user settings
 */
export async function getUserSettings(userId) {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (userSnap.exists()) {
    const userData = userSnap.data();
    return userData.userSettings || { dontShowInteractive: false };
  }
  
  return { dontShowInteractive: false };
}

/**
 * Update user settings
 */
export async function updateUserSettings(userId, userSettings) {
  const userRef = doc(db, "users", userId);
  
  await updateDoc(userRef, {
    userSettings: userSettings,
    lastModified: serverTimestamp()
  });
}

/**
 * Increment user text count and return updated stats
 */
export async function incrementUserTextCount(userId) {
  const profileRef = doc(db, "users", userId, "subscription", "profile");
  
  try {
    const profileSnap = await getDoc(profileRef);
    if (profileSnap.exists()) {
      const currentData = profileSnap.data();
      const newTextCount = (currentData.textCount || 0) + 1;
      
      await updateDoc(profileRef, {
        textCount: newTextCount
      });
      
      return {
        currentCount: newTextCount,
        monthlyLimit: currentData.maxTextPerMonth || 50,
        remaining: (currentData.maxTextPerMonth || 50) - newTextCount
      };
    }
  } catch (error) {

    throw error;
  }
}

/**
 * Increment user image count and return updated stats
 */
export async function incrementUserImageCount(userId) {
  const profileRef = doc(db, "users", userId, "subscription", "profile");
  
  try {
    const profileSnap = await getDoc(profileRef);
    if (profileSnap.exists()) {
      const currentData = profileSnap.data();
      const newImageCount = (currentData.imageCount || 0) + 1;
      
      await updateDoc(profileRef, {
        imageCount: newImageCount
      });
      
      return {
        currentCount: newImageCount,
        monthlyLimit: currentData.maxImagesPerMonth || 50,
        remaining: (currentData.maxImagesPerMonth || 50) - newImageCount
      };
    }
  } catch (error) {

    throw error;
  }
}

/**
 * Calculate when the user's counters will next reset
 * based on their original signup date
 */
function getNextResetDate(createdAt, lastResetDate) {
  const resetDate = new Date(lastResetDate);
  const creationDate = new Date(createdAt);
  
  // Get the day of month from creation date
  const resetDay = creationDate.getDate();
  
  // Start with next month from last reset
  resetDate.setMonth(resetDate.getMonth() + 1);
  resetDate.setDate(resetDay);
  
  return resetDate;
}